import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import useAppSetting from 'hooks/use-app-setting'
import { useAppSelector } from 'store'
import type { AnalyticEventConfig } from 'types/analytic-tracking'
import {
  matchesAnalyticPage,
  readUmamiEventAttributes,
  resolveAnalyticEventConfig,
  resolveUmamiEventElement,
} from 'utils/analytic-event-matcher'
import {
  buildAnalyticsEventContext,
  clearAnalyticsRuntime,
  configureAnalyticsRuntime,
  identifyAnalyticsUser,
  loadAnalyticsScript,
  normalizeAnalyticConfig,
  resolveAnalyticsScriptUrl,
  trackAnalyticsEvent,
  trackAnalyticsPageView,
} from 'utils/track-analytics-event'

/** Survives React Strict Mode remounts so identify isn't POSTed twice on startup. */
let lastIdentifySignature: string | null = null

const useAnalyticEventTracking = (): void => {
  // useAppSetting merges branch_settings over application_settings for the current Branch-ID.
  // ANALYTICS_TRACKING_CONFIG is stored per-branch in branch_settings.
  const {
    key: { ANALYTICS_TRACKING_CONFIG: rawAnalyticConfig },
  } = useAppSetting()

  const analyticConfig = useMemo(() => normalizeAnalyticConfig(rawAnalyticConfig), [rawAnalyticConfig])
  const rawScriptUrl = analyticConfig?.scriptUrl ?? ''
  const scriptUrl = resolveAnalyticsScriptUrl(rawScriptUrl)
  const websiteId = analyticConfig?.websiteId ?? ''
  const isAnalyticsEnabled = !!(analyticConfig?.enabled && scriptUrl && websiteId)

  const [isAnalyticsReady, setIsAnalyticsReady] = useState(false)

  const userId = useAppSelector((state) => state.user.user?.id)
  const countryCode = useAppSelector(
    (state) => state.user.selectedBranch?.countryCode || state.user.user?.master?.countryCode || '',
  )
  const eventsKey = JSON.stringify(analyticConfig?.events ?? [])
  const events = useMemo(() => JSON.parse(eventsKey) as AnalyticEventConfig[], [eventsKey])
  const { pathname } = useLocation()

  const clickContextRef = useRef({ pathname, userId, countryCode, events })
  clickContextRef.current = { pathname, userId, countryCode, events }

  // Dedupe page-enter fires for the same route + event config (ignore user/country churn).
  const lastPageViewSignatureRef = useRef<string | null>(null)
  // Dedupe pageviews per pathname: query rewrites (replaceState) must not inflate Pages views.
  const lastPageViewPathRef = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false

    if (!isAnalyticsEnabled) {
      setIsAnalyticsReady(false)
      lastPageViewSignatureRef.current = null
      lastPageViewPathRef.current = null
      lastIdentifySignature = null
      clearAnalyticsRuntime()
      return
    }

    // Direct collect works even if the tracker script fails to expose window.umami.
    setIsAnalyticsReady(configureAnalyticsRuntime(rawScriptUrl, websiteId))

    loadAnalyticsScript(rawScriptUrl, websiteId).then((ok) => {
      if (cancelled) return
      setIsAnalyticsReady(configureAnalyticsRuntime(rawScriptUrl, websiteId) || ok)
    })

    return () => {
      cancelled = true
    }
  }, [isAnalyticsEnabled, scriptUrl, websiteId, rawScriptUrl])

  useEffect(() => {
    if (!isAnalyticsReady || !userId) return

    const signature = `${userId}::${countryCode}::${websiteId}`
    if (lastIdentifySignature === signature) return
    lastIdentifySignature = signature

    identifyAnalyticsUser(String(userId), { countryCode })
  }, [isAnalyticsReady, userId, countryCode, websiteId])

  useEffect(() => {
    if (!isAnalyticsReady || !analyticConfig?.enabled) return

    const handleClick = (event: MouseEvent) => {
      const {
        pathname: currentPath,
        userId: currentUserId,
        countryCode: currentCountry,
        events: currentEvents,
      } = clickContextRef.current

      const context = buildAnalyticsEventContext(currentUserId, currentCountry)

      const resolved = resolveAnalyticEventConfig(event.target, currentPath, currentEvents)
      if (resolved) {
        const { config } = resolved

        trackAnalyticsEvent(config.event, {
          ...config.metadata,
          eventId: config.id,
          ...context,
        })
        return
      }

      // `data-umami-event` needs no `events` entry. The tracker script runs with auto-track off, so
      // this listener is the only sender — every `data-umami-event-*` extra reaches the payload.
      const umamiEvent = readUmamiEventAttributes(resolveUmamiEventElement(event.target))
      if (!umamiEvent) return

      trackAnalyticsEvent(umamiEvent.event, { ...umamiEvent.data, ...context })
    }

    document.addEventListener('click', handleClick, true)
    return () => document.removeEventListener('click', handleClick, true)
  }, [isAnalyticsReady, analyticConfig?.enabled, eventsKey])

  // Pageviews are ours to send now that the tracker script runs with data-auto-track="false".
  useEffect(() => {
    if (!isAnalyticsReady || !analyticConfig?.enabled) return

    if (lastPageViewPathRef.current === pathname) return
    lastPageViewPathRef.current = pathname

    trackAnalyticsPageView(pathname)
  }, [isAnalyticsReady, analyticConfig?.enabled, pathname])

  // Events with no `match.element` are page-enter: fire once per matching route enter.
  useEffect(() => {
    if (!isAnalyticsReady || !analyticConfig?.enabled || !events.length) return

    const signature = `${pathname}::${eventsKey}`
    if (lastPageViewSignatureRef.current === signature) return
    lastPageViewSignatureRef.current = signature

    const { userId: currentUserId, countryCode: currentCountry } = clickContextRef.current

    events
      .filter(
        (config) =>
          config.enabled !== false && !config.match.element && matchesAnalyticPage(config.match.page, pathname),
      )
      .forEach((config) => {
        trackAnalyticsEvent(config.event, {
          ...config.metadata,
          eventId: config.id,
          userId: currentUserId,
          country: currentCountry,
          createTs: new Date().toISOString(),
        })
      })
  }, [isAnalyticsReady, analyticConfig?.enabled, events, eventsKey, pathname])
}

export default useAnalyticEventTracking
